import { type FC, useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useToken } from "@packsize/authentication";
import { type X5Machine } from "@packsize/schemas/machines";

import { baseRoutes } from "@/api/base-routes";
import { useX5Machine } from "@/hooks/machines/x5";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import type { ErrorsAndWarningsProps, MachineFaultDisplay, MachineFaultType } from "@/schemas";

const websocketUrl = `${baseRoutes.x5}/hubs/x5machines`;
const errorsChangedEventName = "OnErrorsUpdated";
const warningsChangedEventName = "OnWarningsUpdated";

type AlarmData = {
	code?: number;
	alarm?: number;
	alarmNumber?: number;
	aux2?: string | number;
};

function getActionKey(code: number, severity: MachineFaultType): string {
	switch (`${severity}${code}`) {
		case "Error1030":
			return "CheckAir";
		case "Error1":
		case "Warning1101":
			return "CheckFollowing";
		case "Error1022":
		case "Error1024":
		case "Error1025":
		case "Error1026":
		case "Error712":
		case "Error713":
			return "CheckLabelPrinter";
		case "Error1032":
		case "Error1034":
			return "CheckScanner";
		case "Error1014":
		case "Error1015":
		case "Error1016":
		case "Error1017":
		case "Error701":
		case "Error702":
		case "Error703":
		case "Error704":
		case "Error705":
		case "Error707":
		case "Error718":
		case "Error725":
		case "Error726":
		case "Error727":
			return "CleanConveyor";
		case "Error1021":
		case "Error1027":
			return "CleanConveyorAndCheckLabelApplicator";
		case "Error502":
			return "CleanBoxFeeder";
		case "Error719":
		case "Error720":
			return "CleanLabelArea";
		case "Error203":
			return "CleanRollers";
		case "Error706":
			return "CleanSpinner";
		case "Error1001":
		case "Error1002":
		case "Error1003":
		case "Error1004":
		case "Error1005":
		case "Error1006":
		case "Error1007":
		case "Error1008":
		case "Error1009":
		case "Error1010":
		case "Error1011":
		case "Error1012":
		case "Error1013":
			return "ClearAndHome";
		case "Error709":
		case "Error710":
		case "Warning603":
		case "Warning710":
			return "DoFollowing";
		default:
			return "";
	}
}

function getResolution(code: number, severity: MachineFaultType, intl: IntlShape): string | undefined {
	const actionKey = getActionKey(code, severity);
	const translationKey = actionKey ? `X5 Action ${actionKey}` : `X5 ${severity} ${code} Action`;
	const resolution = intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey });

	return resolution === translationKey ? undefined : resolution;
}

function createDisplayItems(
	machineId: string,
	data: AlarmData[] | null | undefined,
	severity: MachineFaultType,
	intl: IntlShape,
): MachineFaultDisplay[] {
	if (data === undefined || data === null) {
		return [];
	}

	return data.map((alarm) => {
		const code = alarm.code ?? alarm.alarm ?? alarm.alarmNumber ?? 0;
		const parameter = code === 1 && severity === "Warning" ? alarm.aux2 : undefined;
		const translationKey = `X5 ${severity} ${code}${parameter === undefined ? "" : `-${parameter}`}`;

		return {
			machineId,
			title: intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey }),
			resolution: getResolution(code, severity, intl),
			image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/x5/${severity.toLowerCase()}/${code}.svg`,
			fallbackImage: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/x5/${severity.toLowerCase()}/default.svg`,
			type: severity,
		};
	});
}

const createInitialErrors = (machine: X5Machine | null, intl: IntlShape) => {
	if (machine === null) {
		return [];
	}

	const activeErrors = machine.workstations.flatMap(({ alarms }) => alarms);

	if (activeErrors.length > 0) {
		return createDisplayItems(machine.id, activeErrors, "Error", intl);
	}

	return [];
};

const createInitialWarnings = (machine: X5Machine | null, intl: IntlShape) => {
	if (machine === null) {
		return [];
	}

	const warnings = machine.activeWarnings.map((warning) => ({
		code: warning.code,
		aux2: warning.code === 1 ? warning.arguments[1] : undefined,
	}));

	return createDisplayItems(machine.id, warnings, "Warning", intl);
};

interface X5MachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
	readonly onWarningsChanged: NonNullable<ErrorsAndWarningsProps["onWarningsChanged"]>;
}

const X5MachineErrorsAndWarnings = ({
	machineId,
	onErrorsChanged,
	onWarningsChanged,
}: X5MachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { bearerToken } = useToken();
	const { data: machine } = useX5Machine(machineId);
	const { hubConnection, subscribe, unsubscribe } = useSignalRHub({
		hubUrl: websocketUrl,
		token: bearerToken,
	});

	useEffect(() => {
		if (machine === undefined) {
			return;
		}

		const errors = createInitialErrors(machine, intl);
		const warnings = createInitialWarnings(machine, intl);

		onErrorsChanged(machineId, errors);
		onWarningsChanged(machineId, warnings);
	}, [intl, machine, machineId, onErrorsChanged, onWarningsChanged]);

	useEffect(() => {
		if (hubConnection === undefined) {
			return;
		}

		const handleErrorsChanged = (...args: unknown[]) => {
			const [_tenantId, eventMachineId, errors] = args as [string, string, AlarmData[] | null | undefined];

			if (eventMachineId !== machineId) {
				return;
			}

			onErrorsChanged(machineId, createDisplayItems(machineId, errors, "Error", intl));
		};

		subscribe(errorsChangedEventName, handleErrorsChanged);

		return () => {
			unsubscribe(errorsChangedEventName, handleErrorsChanged);
		};
	}, [hubConnection, intl, machineId, onErrorsChanged, subscribe, unsubscribe]);

	useEffect(() => {
		if (hubConnection === undefined) {
			return;
		}

		const handleWarningsChanged = (...args: unknown[]) => {
			const [_tenantId, eventMachineId, warnings] = args as [string, string, AlarmData[] | null | undefined];

			if (eventMachineId !== machineId) {
				return;
			}

			onWarningsChanged(machineId, createDisplayItems(machineId, warnings, "Warning", intl));
		};

		subscribe(warningsChangedEventName, handleWarningsChanged);

		return () => {
			unsubscribe(warningsChangedEventName, handleWarningsChanged);
		};
	}, [hubConnection, intl, machineId, onWarningsChanged, subscribe, unsubscribe]);

	return null;
};

export const X5ErrorsAndWarnings: FC<Required<ErrorsAndWarningsProps>> = ({
	machines,
	onErrorsChanged,
	onWarningsChanged,
}) => {
	return (
		<>
			{machines
				.filter(({ machineType }) => machineType === "X5" || machineType === "X5wPrint")
				.map(({ machineId }) => (
					<X5MachineErrorsAndWarnings
						key={machineId}
						machineId={machineId}
						onErrorsChanged={onErrorsChanged}
						onWarningsChanged={onWarningsChanged}
					/>
				))}
		</>
	);
};
